import { useMemo } from 'react';
import { AppDialog } from '../../../../../components/common/CustomMdal';
import { Images } from '../../../../../utils/imageConstants';
import { fetchDivisions } from '../../../../myTeam/api/myTeamApi';
import type { IDivisionResponse } from '../../../../myTeam/types/myTeam';
import { useQuery } from '@tanstack/react-query';
import { QUERY_KEY } from '../../../../../services/queryEndPoint';
import { resolvePublicImageUrl } from '../../../../../helper/helper';
import './TeamSelectModal.scss';

export interface TeamOption {
  teamId: string;
  teamName: string;
  teamSnapshotId: string;
  captainPlayerId?: string | null;
  /** Raw logo path/URL from API (resolve with resolvePublicImageUrl for img src). */
  imageUrl?: string | null;
}

interface TeamSelectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tournamentId: string | null;
  divisionId: string | null;
  groupId: string | null;
  onSelectTeam: (team: TeamOption) => void;
}

export function flattenDivisionTeams(divisions: IDivisionResponse[]): TeamOption[] {
  const seen = new Set<string>();
  const teams: TeamOption[] = [];

  const visit = (division: IDivisionResponse) => {
    const divisionTeams = Array.isArray(division.teams) ? division.teams : [];
    divisionTeams.forEach((team) => {
      const entry = team as {
        teamSnapshotId?: string;
        teamId?: string;
        teamName?: string;
        imageUrl?: string | null;
        teamLogo?: string | null;
        captainPlayerId?: string | null;
        team?: {
          teamId?: string;
          teamName?: string;
          imageUrl?: string | null;
          teamLogo?: string | null;
        };
      };
      const teamId = entry.team?.teamId ?? entry.teamId ?? '';
      const teamName = entry.team?.teamName ?? entry.teamName ?? '';
      if (!teamId || !teamName || seen.has(teamId)) return;
      seen.add(teamId);
      const imageUrl =
        entry.team?.imageUrl ??
        entry.team?.teamLogo ??
        entry.imageUrl ??
        entry.teamLogo ??
        null;
      teams.push({
        teamId,
        teamName,
        teamSnapshotId: entry.teamSnapshotId ?? '',
        captainPlayerId: entry.captainPlayerId,
        imageUrl,
      });
    });
    (division.childDivisions ?? []).forEach(visit);
  };
  divisions.forEach(visit);
  return teams;
}

/** Teams for the selected division (and group when the division has child groups). */
export function getTeamsForDivisionAndGroup(
  divisions: IDivisionResponse[],
  divisionId: string | null,
  groupId: string | null,
): TeamOption[] {
  if (!divisionId?.trim()) return [];
  const selectedDivision = divisions.find((d) => d.divisionId === divisionId);
  const childDivisions = selectedDivision?.childDivisions ?? [];
  const hasGroups = childDivisions.length > 0;
  if (hasGroups && !groupId?.trim()) return [];
  const targetGroup =
    hasGroups && groupId ? childDivisions.find((g) => g.divisionId === groupId) : null;
  const targetDivisions = targetGroup ? [targetGroup] : selectedDivision ? [selectedDivision] : [];
  const fromApi = flattenDivisionTeams(targetDivisions);
  if (fromApi.length > 0) return fromApi;
  if (divisionId && groupId) {
    return [
      { teamId: 'demo1', teamName: 'Khanpur CA U-16', teamSnapshotId: 'demo1', captainPlayerId: 'p1' },
      { teamId: 'demo2', teamName: 'Mumbai Warriors', teamSnapshotId: 'demo2', captainPlayerId: null },
      { teamId: 'demo3', teamName: 'Delhi Capitals', teamSnapshotId: 'demo3', captainPlayerId: null },
    ];
  }
  return [];
}

/** Resolve parent division + group when both teams appear in the same tournament division leaf. */
export function findDivisionGroupForTeamIds(
  divisions: IDivisionResponse[],
  teamIdA: string,
  teamIdB: string,
): { divisionId: string; groupId: string } | null {
  const a = teamIdA.trim();
  const b = teamIdB.trim();
  if (!a || !b) return null;
  for (const d of divisions) {
    const children = d.childDivisions ?? [];
    if (children.length > 0) {
      for (const g of children) {
        const opts = getTeamsForDivisionAndGroup(divisions, d.divisionId, g.divisionId);
        if (opts.some((t) => t.teamId === a) && opts.some((t) => t.teamId === b)) {
          return { divisionId: d.divisionId, groupId: g.divisionId };
        }
      }
    } else {
      const opts = getTeamsForDivisionAndGroup(divisions, d.divisionId, null);
      if (opts.some((t) => t.teamId === a) && opts.some((t) => t.teamId === b)) {
        return { divisionId: d.divisionId, groupId: '' };
      }
    }
  }
  return null;
}

export default function TeamSelectModal({
  open,
  onOpenChange,
  tournamentId,
  divisionId,
  groupId,
  onSelectTeam,
}: TeamSelectModalProps) {
  const { data: divisionsPayload } = useQuery({
    queryKey: [QUERY_KEY.getDivisions, tournamentId],
    queryFn: () => (tournamentId ? fetchDivisions(tournamentId) : Promise.resolve({ divisionResponseList: [] })),
    enabled: open && !!tournamentId,
  });

  const divisions = divisionsPayload?.divisionResponseList ?? [];

  const teams = useMemo(
    () => getTeamsForDivisionAndGroup(divisions, divisionId, groupId),
    [divisions, divisionId, groupId],
  );

  const handleSelect = (team: TeamOption) => {
    onSelectTeam(team);
    onOpenChange(false);
  };

  return (
    <AppDialog open={open} onOpenChange={onOpenChange} title="Select Team" size="md" className="team-select-dialog">
      <div className="team-select-list">
        {teams.length === 0 ? (
          <p className="team-select-empty">
            {!divisionId ? 'Select Division and Group first' : 'No teams in this division/group'}
          </p>
        ) : (
          teams.map((team) => (
            <button
              key={team.teamId}
              type="button"
              className="team-select-row"
              onClick={() => handleSelect(team)}
            >
              <img
                src={resolvePublicImageUrl(team.imageUrl) || Images.team_logo}
                alt=""
                className="team-select-icon"
              />
              <span className="team-select-name">{team.teamName}</span>
            </button>
          ))
        )}
      </div>
    </AppDialog>
  );
}
